import { Button, Spinner, Text } from '@/ui-kit'
import { formatNumber } from '@/utils'
import { Clock, Star, TriangleAlert } from 'lucide-react'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMission } from '../api'
import { TYPE_TEXT_MAP } from '../api/models'
import { useMissionsUrl } from '../context/MissionsUrlProvider'
import { BackToListButton } from './BackToListButton'
import { MissionBadge, STATUS_TEXT_MAP } from './MissionBadge'
import { MissionContent } from './MissionContent'
import { MissionStats } from './MissionStats'

export const MissionDetail = ({ missionId }: { missionId: string }) => {
  const { missionsUrl, platform } = useMissionsUrl()

  const { data: mission, isFetching } = useMission(missionId)
  const navigate = useNavigate()

  const onBackClick = () => {
    navigate(missionsUrl)
  }

  useEffect(() => {
    if (mission && mission.type === 'STAKING') {
      navigate(`${missionsUrl}?missionId=${mission.id}`)
    }
  }, [mission, missionsUrl, navigate])

  if (!mission && isFetching) {
    return (
      <div className="flex w-full justify-center py-8">
        <Spinner className="mt-8" />
      </div>
    )
  }

  if (!mission && !isFetching) {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <Text type="h3" className="mb-4">
          Mission not found
        </Text>
        <Button onClick={onBackClick}>Back to Missions</Button>
      </div>
    )
  }

  return mission ? (
    <div className="flex flex-col gap-6 py-8">
      <div className="flex flex-wrap justify-between gap-1 gap-y-3">
        <BackToListButton href={missionsUrl} />
        {mission.view.tooltip && (
          <div className="border-orange/30 ml-auto flex max-w-[600px] min-w-[300px] items-center gap-5 rounded-lg border border-solid px-5 py-3">
            <TriangleAlert className="text-orange shrink-0" />
            <Text type="body2-regular" className="text-gray-70 text-wrap">
              {mission.view.tooltip}
            </Text>
          </div>
        )}
        <div className="ml-auto flex justify-between gap-1">
          {!mission.availability.available ? (
            <MissionBadge>{'Unavailable'}</MissionBadge>
          ) : (
            <>
              <MissionBadge
                variant={
                  mission.view.latestSubmissionStatus === 'FAILED'
                    ? 'error'
                    : mission.view.latestSubmissionStatus === 'COMPLETED' && mission.view.nextSubmissionAvailable
                      ? 'error'
                      : mission.view.nextSubmissionAvailable || mission.view.latestSubmissionStatus === 'COMPLETED'
                        ? 'success'
                        : 'default'
                }
              >
                {mission.view.latestSubmissionStatus === 'SUBMITTED' ? (
                  <>
                    <Clock size={15} /> {STATUS_TEXT_MAP.SUBMITTED}
                  </>
                ) : mission.view.latestSubmissionStatus === 'COMPLETED' && mission.view.nextSubmissionAvailable ? (
                  'Attention'
                ) : (
                  STATUS_TEXT_MAP[mission.view.latestSubmissionStatus || 'null'] || 'New'
                )}
              </MissionBadge>
              {!!mission.tags.length && <MissionBadge variant={'warning'}>{mission.tags[0]}</MissionBadge>}
            </>
          )}
          <MissionBadge variant="default">{TYPE_TEXT_MAP[mission.type] || mission.type}</MissionBadge>
          <MissionBadge variant="success">
            <Star size={15} /> {formatNumber(mission.reward, 0)} points
          </MissionBadge>
        </div>
      </div>
      <div className="flex flex-col">
        <div className="flex flex-col gap-2 border-b border-solid border-gray-100 pb-6">
          <Text type="h4">{mission.title}</Text>
          <MissionStats mission={mission} />
          {mission.shortDescription && (
            <Text type="body1-regular" className="text-gray-80 mt-2 max-w-[880px]">
              {mission.shortDescription}
            </Text>
          )}
        </div>
        <MissionContent mission={mission} />
      </div>
    </div>
  ) : (
    <></>
  )
}
